import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getAllowedUsers,
  addAllowedUser,
  removeAllowedUser,
  toggleAdminRole,
  isSuperAdmin,
} from '../services/adminService';

export default function AdminPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Guard: only admins may view this page. ProtectedRoute checks isAllowed,
  // but not isAdmin, so we enforce it here.
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/host', { replace: true });
    }
  }, [authLoading, isAdmin, navigate]);

  const loadUsers = async () => {
    try {
      const list = await getAllowedUsers();
      setUsers(list.sort((a, b) => (a.email || '').localeCompare(b.email || '')));
    } catch {
      setError('Kunne ikkje laste brukarar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadUsers();
  }, [isAdmin]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setError('Skriv inn ei gyldig e-postadresse');
      return;
    }
    setError('');
    try {
      await addAllowedUser(email, user.email);
      setNewEmail('');
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Kunne ikkje legge til brukar');
    }
  };

  const handleRemove = async (docId) => {
    try {
      await removeAllowedUser(docId);
      setConfirmDelete(null);
      await loadUsers();
    } catch {
      setError('Kunne ikkje fjerne brukar');
    }
  };

  const handleToggleAdmin = async (docId, currentIsAdmin) => {
    try {
      await toggleAdminRole(docId, !currentIsAdmin);
      await loadUsers();
    } catch {
      setError('Kunne ikkje endre admin-rolle');
    }
  };

  // While AuthContext is verifying admin status, show a simple loader to avoid flicker
  if (authLoading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background-light font-display flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-eq-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-background-light font-display text-[#0d121b] pb-28 overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 flex items-center bg-background-light/95 backdrop-blur-sm p-4 justify-between transition-colors duration-300">
        <div className="flex items-center gap-3">
          <Link
            to="/host"
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/80 transition-colors"
            title="Tilbake til oversikta"
          >
            <span className="material-symbols-outlined text-gray-600">arrow_back</span>
          </Link>
          <div>
            <p className="text-sm font-medium text-gray-500 leading-none">Admin</p>
            <h2 className="text-[#0d121b] text-xl font-bold leading-tight">Innstillingar</h2>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 max-w-2xl w-full mx-auto">
        {/* Section: Access & users */}
        <section className="bg-white rounded-2xl shadow-ambient p-6 mb-6">
          <div className="mb-4">
            <h3 className="text-lg font-bold text-gray-900">Tilgang og brukarar</h3>
            <p className="text-sm text-gray-500 mt-1">
              Styr kven som kan logge inn på Mikkel Kviss.
            </p>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-5 text-sm text-blue-700">
            <span className="font-semibold">Tips:</span> Alle med @mikkelparken.no-epost har
            automatisk tilgang. Legg til andre brukarar under.
          </div>

          {/* Add user form */}
          <form onSubmit={handleAdd} className="flex gap-2 mb-5">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="ny.brukar@epost.no"
              className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-eq-primary/30 focus:border-eq-primary"
            />
            <button
              type="submit"
              className="btn-gradient text-white px-5 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap"
            >
              Legg til
            </button>
          </form>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2.5 mb-4 text-sm">
              {error}
            </div>
          )}

          {/* User list */}
          {loading ? (
            <div className="text-center py-8 text-gray-400">Lastar...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              Ingen eksterne brukarar lagt til endå. @mikkelparken.no-brukarar har
              automatisk tilgang.
            </div>
          ) : (
            <div className="space-y-2">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{u.email}</p>
                    <p className="text-xs text-gray-400">
                      {u.isAdmin ? 'Admin' : 'Brukar'}
                      {u.addedBy && ` · Lagt til av ${u.addedBy}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-3 shrink-0">
                    {!isSuperAdmin(u.email) && (
                      <button
                        onClick={() => handleToggleAdmin(u.id, u.isAdmin)}
                        title={u.isAdmin ? 'Fjern admin' : 'Gjer til admin'}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <span
                          className={`material-symbols-outlined text-lg ${
                            u.isAdmin ? 'text-amber-500' : 'text-gray-400'
                          }`}
                        >
                          shield_person
                        </span>
                      </button>
                    )}
                    {confirmDelete === u.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleRemove(u.id)}
                          className="text-xs bg-red-500 text-white px-2.5 py-1 rounded-lg font-semibold"
                        >
                          Slett
                        </button>
                        <button
                          onClick={() => setConfirmDelete(null)}
                          className="text-xs text-gray-500 px-2 py-1"
                        >
                          Avbryt
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDelete(u.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors"
                        title="Fjern brukar"
                      >
                        <span className="material-symbols-outlined text-lg text-gray-400 hover:text-red-500">
                          delete
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
